import React from 'react';
import { Box, Grid, Stack, Typography, IconButton } from '@mui/material';
import { ReactComponent as ExpandIcon } from '../../../assets/PepWise2.0/Expand.svg';
import KpiTile from './KpiTile';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import LoaderComponent from 'components/Loader';
import { InfoTooltip, TooltipLabel } from '../InfoTooltip';
import { useTheme } from '@mui/material/styles';

export type KpiTrend = {
  value?: number | string;
  changeType?: 'increase' | 'decrease';
  reverseTrendColor?: boolean;
};

export type KpiTileConfig = {
  id: string;

  title: any;
  titleKey?: string;

  number?: number | string | number[] | string[] | null;
  units?: string | string[] | null;
  pbiUnits?: string | string[] | null;

  tooltipLabel?: TooltipLabel;
  hasTooltip?: boolean;
  isHappySignalTooltip?: boolean;

  unitIcon?: null | string;
  unitIconColor?: null | string;

  trend?: KpiTrend;
  showChangePercentage?: boolean;

  bgColor?: string;
  color?: string;

  subText?: string;
  sectorLabel?: string;

  isLoading?: boolean;
  isInProgress?: boolean;
  hidden?: boolean;

  onOpen?: () => void;
};

export interface KpiTileGroupProps {
  title: string;
  hasTooltip?: boolean;
  tooltipLabel?:
    | string
    | JSX.Element
    | {
        title: string;
        description: string | React.ReactNode;
      }[];
  tiles: KpiTileConfig[];
  columns?: number;
  defaultExpanded?: boolean;
  showExpandIcon?: boolean;
  isLoading: boolean;

  onTileOpen?: (id: string) => void;
  onExpand?: () => void;
}

const KpiTileItem = ({
  tile,
  onTileOpen,
}: {
  tile: KpiTileConfig;
  onTileOpen?: (id: string) => void;
}) => {
  if (tile.hidden) return null;

  return (
    <KpiTile
      title={tile.title}
      titleKey={tile.titleKey}
      number={tile.number}
      units={tile.units}
      unitIcon={tile.unitIcon}
      unitIconColor={tile.unitIconColor}
      tooltipLabel={tile.tooltipLabel || ''}
      hasTooltip={tile.hasTooltip}
      bgColor={tile.bgColor}
      color={tile.color}
      isLoading={tile.isLoading ?? false}
      isInProgress={tile.isInProgress}
      change={tile.trend?.value}
      changeType={tile.trend?.changeType}
      reverseTrendColor={tile.trend?.reverseTrendColor}
      showChangePercentage={tile.showChangePercentage}
      subText={tile.subText}
      sectorLabel={tile.sectorLabel}
      handleEvent={() => {
        tile.onOpen?.();
        onTileOpen?.(tile.id);
      }}
    />
  );
};

/* ======================================================
   KPI Tile Group
====================================================== */

const KpiTileGroup = ({
  title,
  isLoading = false,
  tooltipLabel,
  hasTooltip = true,
  tiles,
  columns = 2,
  defaultExpanded = false,
  onTileOpen,
  showExpandIcon,
  onExpand,
}: KpiTileGroupProps) => {

  const theme = useTheme();
  const colors = theme.palette;
  
  const handleExpandClick = () => {
    onExpand?.();
  };


  return (
    <Box
      sx={{
        backgroundColor: colors.kpiTile?.background?.card,
        border: `1px solid ${colors.kpiTile?.background?.border}`,
        p: 2,
      }}
    >
      {!isLoading ? (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center">
              <Typography
                variant="widgetTitle"
                sx={{ color: `${colors.widget.text.title} !important` }}
              >
                <HighlightedText text={title} />
              </Typography>
              {hasTooltip && !!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
            </Stack>
            {!!showExpandIcon && (
              <IconButton
                size="small"
                onClick={handleExpandClick}
                aria-label={'Expand KPI group'}
              >
                <ExpandIcon />
              </IconButton>
            )}
          </Stack>

          <Grid container spacing={1}>
            {tiles.map((tile) => {
              const isFullWidth = Array.isArray(tile.number) && tile.number.length > 1;

              return (
                <Grid key={tile.id} item xs={12} sm={12} md={isFullWidth ? 12 : 12 / columns}>
                  <KpiTileItem tile={tile} onTileOpen={onTileOpen} />
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : (
        <LoaderComponent />
      )}
    </Box>
  );
};

export default KpiTileGroup;
